import { useState, useEffect } from 'react';
import { Calendar, Clock, MapPin, Loader2 } from 'lucide-react';
import usePageMeta from '@/hooks/usePageMeta';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DAY_LABELS = { friday: 'Friday, Aug 21', saturday: 'Saturday, Aug 22', sunday: 'Sunday, Aug 23' };
const DAY_ORDER = ['friday', 'saturday', 'sunday'];

const SchedulePage = () => {
  const [schedule, setSchedule] = useState({ settings: {}, items: [] });
  const [loading, setLoading] = useState(true);
  const [activeDay, setActiveDay] = useState('friday');

  usePageMeta({
    title: 'Event Schedule - Elevated Escape 2026',
    description: 'Full weekend schedule for The Elevated Escape 2026 camping retreat. Music, workshops, yoga, meals, and more — August 21-23 at Camp Waub-O-Jeeg.',
    image: '/images/site/sponsor.jpg',
    url: 'https://elevatingmn.org/schedule',
  });

  useEffect(() => {
    fetch(`${API}/schedule`).then(r => r.json()).then(data => {
      setSchedule(data);
      const days = [...new Set((data.items || []).map(i => i.day))];
      if (days.length > 0) setActiveDay(days.sort((a, b) => DAY_ORDER.indexOf(a) - DAY_ORDER.indexOf(b))[0]);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-brand-gold" />
      </div>
    );
  }

  if (!schedule.settings?.enabled) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-6" data-testid="schedule-disabled">
        <div className="text-center max-w-md">
          <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-brand-dark mb-2">Schedule Coming Soon</h2>
          <p className="text-gray-600">The event schedule will be published closer to the event date. Check back soon!</p>
        </div>
      </div>
    );
  }

  const dayItems = (schedule.items || []).filter(i => i.day === activeDay);
  const availableDays = DAY_ORDER.filter(d => (schedule.items || []).some(i => i.day === d));

  return (
    <div className="min-h-screen bg-gray-50" data-testid="schedule-page">
      {/* Hero */}
      <section className="bg-brand-dark py-16">
        <div className="mx-auto max-w-5xl px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-3" data-testid="schedule-title">
            {schedule.settings.page_title || 'Event Schedule'}
          </h1>
          <p className="text-brand-gold text-lg">{schedule.settings.page_subtitle || ''}</p>
        </div>
      </section>

      {/* Day Tabs */}
      <div className="bg-white border-b sticky top-0 z-20">
        <div className="mx-auto max-w-5xl px-6 lg:px-8">
          <div className="flex gap-1" data-testid="day-tabs">
            {availableDays.map(day => (
              <button
                key={day}
                onClick={() => setActiveDay(day)}
                className={`px-6 py-4 text-sm font-medium capitalize border-b-2 transition-colors ${
                  activeDay === day
                    ? 'border-brand-gold text-brand-dark'
                    : 'border-transparent text-gray-500 hover:text-brand-dark'
                }`}
                data-testid={`day-tab-${day}`}
              >
                {DAY_LABELS[day] || day}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Schedule Items */}
      <section className="mx-auto max-w-5xl px-6 lg:px-8 py-8">
        {dayItems.length === 0 ? (
          <p className="text-gray-500 text-center py-12">No events scheduled for this day yet.</p>
        ) : (
          <div className="space-y-0" data-testid="schedule-items">
            {dayItems.map((item, i) => (
              <div key={item.id} className="flex gap-4 sm:gap-6 group" data-testid={`schedule-item-${item.id}`}>
                {/* Time column */}
                <div className="w-24 sm:w-28 flex-shrink-0 text-right pt-6">
                  <p className="text-sm font-bold text-brand-dark">{item.time_start}</p>
                  {item.time_end && <p className="text-xs text-gray-400">{item.time_end}</p>}
                </div>
                {/* Timeline dot + line */}
                <div className="flex flex-col items-center flex-shrink-0">
                  <div className="w-3 h-3 rounded-full bg-brand-gold mt-7 z-10" />
                  {i < dayItems.length - 1 && <div className="w-px flex-1 bg-gray-200" />}
                </div>
                {/* Content */}
                <div className="flex-1 pb-8 pt-4">
                  <div className="bg-white border p-4 sm:p-5 hover:shadow-sm transition-shadow">
                    <h3 className="font-semibold text-brand-dark text-base">{item.title}</h3>
                    {item.description && <p className="text-gray-600 text-sm mt-1">{item.description}</p>}
                    {item.location && (
                      <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> {item.location}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default SchedulePage;
